/**
 * frontend/src/pages/Security.js
 * 
 * Public security information page.
 * Showcases ChangeFox's commitment to data protection, encryption standards,
 * infrastructure security, and compliance certifications (SOC 2, GDPR).
 */
import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiShield, FiLock, FiServer, FiEye, FiCheckCircle } from 'react-icons/fi';
import BackgroundShapes from '../components/common/BackgroundShapes';
import Footer from '../components/common/Footer';
import SEO from '../components/common/SEO';
import logo from '../assets/logo.png';
import '../styles/landing.css';

const Security = () => {
  const securityFeatures = [
    {
      icon: <FiLock />,
      title: 'Data Encryption',
      description: 'All data is encrypted in transit using TLS 1.3 and at rest using AES-256 encryption.',
      color: '#6366f1',
    },
    {
      icon: <FiShield />,
      title: 'Access Controls',
      description: 'Role-based access control ensures users only see what they need to see.',
      color: '#8b5cf6',
    },
    {
      icon: <FiServer />,
      title: 'Secure Infrastructure',
      description: 'Our infrastructure is hosted on enterprise-grade cloud providers with SOC 2 compliance.',
      color: '#06b6d4',
    },
    {
      icon: <FiEye />,
      title: 'Audit Logging',
      description: 'Comprehensive audit trails track every action for compliance and security monitoring.',
      color: '#10b981',
    },
  ];

  const certifications = [
    'SOC 2 Type II Compliant',
    'GDPR Compliant',
    'ISO 27001 Standards',
    '99.9% Uptime SLA',
  ];

  return (
    <div className="landing-page">
      <SEO
        title="Security - ChangeFox"
        description="Learn about ChangeFox's enterprise-grade security measures, certifications, and data protection practices."
        keywords="security, SOC 2, GDPR, data protection, encryption, compliance"
        path="/security"
      />
      <BackgroundShapes />

      {/* Navigation */}
      <motion.nav
        className="landing-nav"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Container>
          <div className="nav-content">
            <Link to="/" className="nav-logo">
              <img src={logo} alt="ChangeFox" className="nav-logo-img" />
            </Link>
            <div className="nav-links">
              <Link to="/" className="nav-link">Home</Link>
              <Link to="/login" className="nav-link">Sign In</Link>
            </div>
          </div>
        </Container>
      </motion.nav>

      {/* Content */}
      <section className="legal-section">
        <Container>
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="section-badge light">Security</div>
            <h1 className="section-title light">Enterprise-Grade Security</h1>
            <p className="section-subtitle light">
              Your data security is our top priority. We employ industry-leading security measures
              to protect your compliance data.
            </p>
          </motion.div>

          <Row className="g-4 mt-4">
            {securityFeatures.map((feature, index) => (
              <Col key={index} md={6}>
                <motion.div
                  className="security-card"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                >
                  <div
                    className="security-icon"
                    style={{ background: `${feature.color}15`, color: feature.color }}
                  >
                    {feature.icon}
                  </div>
                  <div className="security-text">
                    <h3 className="security-title">{feature.title}</h3>
                    <p className="security-description">{feature.description}</p>
                  </div>
                </motion.div>
              </Col>
            ))}
          </Row>

          <motion.div
            className="certifications-section"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <h2>Compliance & Certifications</h2>
            <div className="certifications-grid">
              {certifications.map((cert, index) => (
                <div key={index} className="certification-item">
                  <FiCheckCircle />
                  <span>{cert}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            className="legal-content mt-5"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <div className="legal-body">
              <h2>Our Security Practices</h2>

              <h3>Infrastructure Security</h3>
              <p>
                ChangeFox is hosted on secure cloud infrastructure with multiple layers of protection.
                Our servers are located in SOC 2 certified data centers with 24/7 physical security,
                biometric access controls, and redundant power systems.
              </p>

              <h3>Application Security</h3>
              <p>
                We follow secure development practices including regular code reviews, automated security
                testing, and penetration testing by third-party security firms. Our application implements:
              </p>
              <ul>
                <li>Input validation and output encoding</li>
                <li>Protection against OWASP Top 10 vulnerabilities</li>
                <li>Secure session management</li>
                <li>Multi-factor authentication support</li>
              </ul>

              <h3>Data Protection</h3>
              <p>
                All customer data is encrypted both in transit and at rest. We use industry-standard
                encryption protocols and maintain strict key management practices. Regular backups are
                performed and stored in geographically distributed locations.
              </p>

              <h3>Incident Response</h3>
              <p>
                We maintain a comprehensive incident response plan and dedicated security team. In the
                unlikely event of a security incident, affected customers will be notified promptly in
                accordance with applicable regulations.
              </p>

              <h3>Report a Vulnerability</h3>
              <p>
                If you discover a security vulnerability, please report it to us at{' '}
                <a href="mailto:contact@changefox.ai">contact@changefox.ai</a>. We take all reports
                seriously and will investigate promptly.
              </p>
            </div>
          </motion.div>
        </Container>
      </section>

      <Footer />
    </div>
  );
};

export default Security;
